import React, { useEffect, useState } from "react";
import {
  LIST_PAGE_SIZE_DEFAULT,
  LIST_PAGE_SIZE_OPTIONS,
  clampPage,
  formatPaginationCount,
  getPaginationMeta,
} from "../shared/utils/listPagination";

/**
 * Table/list pagination bar: range summary, page size select, prev/next and page jump.
 */
export default function ListPaginationBar({
  totalCount = 0,
  page = 1,
  pageSize = LIST_PAGE_SIZE_DEFAULT,
  pageSizeOptions = LIST_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
  className = "",
}) {
  const { totalPages, safePage, rangeStart, rangeEnd } = getPaginationMeta(totalCount, page, pageSize);
  const [pageInput, setPageInput] = useState(String(safePage));

  useEffect(() => {
    setPageInput(String(safePage));
  }, [safePage]);

  const commitPageInput = () => {
    const parsed = parseInt(pageInput, 10);
    const next = clampPage(Number.isFinite(parsed) ? parsed : safePage, totalPages);
    setPageInput(String(next));
    if (next !== safePage) onPageChange(next);
  };

  const rootClass = ["list-pagination-bar", className].filter(Boolean).join(" ");

  return (
    <div className={rootClass} role="navigation" aria-label="List pagination">
      <div className="list-pagination-bar__range">
        <span className="list-pagination-bar__label">Showing:</span>{" "}
        <span className="list-pagination-bar__value">
          {rangeStart} - {rangeEnd} of {formatPaginationCount(totalCount)}
        </span>
      </div>

      <div className="list-pagination-bar__size">
        <span className="list-pagination-bar__label">Displaying:</span>{" "}
        <select
          className="list-pagination-bar__select"
          value={pageSize}
          aria-label="Rows per page"
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next) && next > 0) onPageSizeChange(next);
          }}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>{" "}
        <span className="list-pagination-bar__suffix">per page</span>
      </div>

      <div className="list-pagination-bar__nav">
        <button
          type="button"
          className={`btn-link ${safePage <= 1 ? "btn-link-muted" : ""}`}
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
        >
          Prev Page
        </button>
        <input
          type="text"
          inputMode="numeric"
          className="list-pagination-bar__page-input"
          value={pageInput}
          aria-label="Current page"
          onChange={(e) => setPageInput(e.target.value)}
          onBlur={commitPageInput}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitPageInput();
            }
          }}
        />
        <button
          type="button"
          className={`btn-link ${safePage >= totalPages ? "btn-link-muted" : ""}`}
          disabled={safePage >= totalPages || totalCount === 0}
          onClick={() => onPageChange(safePage + 1)}
        >
          Next page
        </button>
      </div>
    </div>
  );
}

